import {clsx, type ClassValue} from "clsx"
import {twMerge} from "tailwind-merge"
import {TransactionStatusesEnum} from "@/lib/enums/TransactionStatusesEnum";
import {ENG_LOCALES} from "@/lib/utils/contants/engLocales";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export const parseJwt = (token: string) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error("Could not parse JWT token", e);
        return null;
    }
}

export const getTxStatusColor = (status: TransactionStatusesEnum) => {
    switch (status) {
        case TransactionStatusesEnum.CANCELLED:
            return 'text-happy-orange-600';
        case TransactionStatusesEnum.FAILED:
        case TransactionStatusesEnum.REJECTED:
            return 'text-red-power-600';
        case TransactionStatusesEnum.PENDING:
        case TransactionStatusesEnum.PENDING_APPROVAL:
            return 'text-purple-solid-secondary';
        default:
            return 'text-electric-green';
    }
}

export const removeDotsFromKeys = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(removeDotsFromKeys);
    }

    if (obj !== null && typeof obj === "object") {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            const newKey = key.replace(/\./g, "_"); // 👈 replace dots
            acc[newKey] = removeDotsFromKeys(value);
            return acc;
        }, {} as Record<string, any>);
    }

    return obj;
};

export const restoreDotsInKeys = (obj: any): any => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        const originalKey = key.replace(/_/g, ".");
        acc[originalKey] = value;
        return acc;
    }, {} as Record<string, any>);
};

export const selectLocaleFromEnOrEs = (locale: string) => {
    return ENG_LOCALES.includes(locale) ? 'en' : 'es';
}

const keyStr =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";

const triplet = (e1: number, e2: number, e3: number) =>
    keyStr.charAt(e1 >> 2) +
    keyStr.charAt(((e1 & 3) << 4) | (e2 >> 4)) +
    keyStr.charAt(((e2 & 15) << 2) | (e3 >> 6)) +
    keyStr.charAt(e3 & 63);

export const rgbDataURL = (r: number, g: number, b: number) =>
    `data:image/gif;base64,R0lGODlhAQABAPAA${
        triplet(0, r, g) + triplet(b, 255, 255)
    }/yH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==`;

/**
 * `crypto.randomUUID` requires a secure context and Chrome 92+; some in-app/
 * affiliate WebViews spoof a modern UA while running an older engine that
 * lacks it (AC-796). `crypto.getRandomValues` is supported far more broadly,
 * so fall back to building a UUID v4 from it. IDs generated here are
 * non-secret analytics/device identifiers, not security tokens, so the
 * `Math.random` last resort (engines lacking Web Crypto entirely) is fine.
 */
export const generateUuid = (): string => {
    if (typeof crypto?.randomUUID === "function") {
        return crypto.randomUUID();
    }

    if (typeof crypto?.getRandomValues === "function") {
        const bytes = crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;

        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};
